import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, SkipForward, Pause, Play, VolumeX, Volume2, Check } from "lucide-react";
import { isSoundEnabled, setSoundEnabled } from "@/lib/chime";
import { FacialExerciseSVG } from "@/components/FacialExerciseSVG";

// Ceremony step order — a full sequence in the order it should be done.
// 1. Wake and drain the tissues, 2. cheek lift with muscles, 3. jaw glide,
// 4. under-eye tap, 5. full-face O-to-E finisher, 6. closing affirmation.
// Total ~4 minutes.
const CEREMONY_ORDER = [
  { template_id: "core-face-stretch-drain",  seconds: 40, cue: "Open wide, then sweep down the neck." },
  { template_id: "core-face-cheek-lift",     seconds: 50, cue: "Smile with your cheeks, not your lips — lift and hold." },
  { template_id: "core-face-jaw-glide",      seconds: 50, cue: "Up the jawline, then down the neck to drain." },
  { template_id: "core-face-eye-tap",        seconds: 30, cue: "Featherlight taps, temple to nose." },
  { template_id: "core-face-full-yoga",      seconds: 40, cue: "O — hold. E — hold. Ten rounds." },
  { template_id: "core-self-love-affirmation", seconds: 30, cue: "Say it out loud, or in the mirror." },
];

export default function CoreCeremony({ items, open, onClose, onCompletedAll }) {
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(CEREMONY_ORDER[0].seconds);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(!isSoundEnabled());
  const [finished, setFinished] = useState(false);
  const tickRef = useRef(null);

  // Merge ceremony order with the plan items we actually have so we can pass through titles/descriptions.
  const steps = CEREMONY_ORDER.map((s) => {
    const it = items.find((i) => i.template_id === s.template_id);
    return it ? { ...s, item: it } : null;
  }).filter(Boolean);

  const total = steps.length;
  const current = steps[idx];

  // Reset when opened
  useEffect(() => {
    if (open) {
      setIdx(0);
      setRemaining(steps[0]?.seconds || 0);
      setPaused(false);
      setFinished(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Timer
  useEffect(() => {
    if (!open || finished || paused || !current) return;
    tickRef.current = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [open, finished, paused, current]);

  // Advance
  useEffect(() => {
    if (!open || finished || !current) return;
    if (remaining > 0) return;
    // Step ended
    if (idx + 1 >= total) {
      setFinished(true);
      // Fire the callback with the item_ids we walked through
      const completedIds = steps.map((s) => s.item.item_id);
      onCompletedAll?.(completedIds);
      return;
    }
    // Move to next step
    setIdx(idx + 1);
    setRemaining(steps[idx + 1].seconds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const skip = () => {
    setRemaining(0);
  };

  const toggleMute = () => {
    setSoundEnabled(muted); // muted currently true → enable; false → disable
    setMuted(!muted);
  };

  if (!open) return null;
  if (steps.length === 0) return null;

  const step = current;
  const pct = step ? Math.round(((step.seconds - remaining) / step.seconds) * 100) : 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-y-auto flex justify-center items-start py-6 sm:py-10 px-4"
      data-testid="core-ceremony"
    >
      {/* soft warm veil */}
      <div className="fixed inset-0 bg-gradient-to-br from-[hsl(var(--paper))]/95 via-[hsl(var(--sand))]/90 to-[hsl(var(--paper))]/95 backdrop-blur-sm" />

      <div className="relative w-[min(560px,92vw)] p-8 md:p-10 rounded-md border hairline bg-[hsl(var(--paper))] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] my-0 self-start">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            {finished ? "Ceremony complete" : `Step ${idx + 1} of ${total}`}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleMute}
              className="p-2 rounded-full hover:bg-[hsl(var(--sand))] text-[hsl(var(--muted-foreground))]"
              aria-label={muted ? "Unmute chimes" : "Mute chimes"}
              data-testid="ceremony-mute"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-[hsl(var(--sand))] text-[hsl(var(--muted-foreground))]"
              aria-label="Close ceremony"
              data-testid="ceremony-close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {!finished ? (
          <>
            <div className="mt-6 grid grid-cols-[auto,1fr] gap-6 items-center">
              <CountdownRing seconds={remaining} pct={pct} />
              <div className="min-w-0">
                <h2 className="font-display text-3xl md:text-4xl italic leading-tight" data-testid="ceremony-title">
                  {step.item.title}
                </h2>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{step.item.description}</p>
                <p className="mt-3 font-display text-lg italic text-[hsl(var(--clay))]">{step.cue}</p>
              </div>
            </div>

            {step.template_id?.startsWith("core-face-") && (
              <div className="mt-6 grid place-items-center" data-testid={`ceremony-svg-${step.template_id}`}>
                <div className="p-4 rounded-full bg-[hsl(var(--sand))]/40 border hairline">
                  <FacialExerciseSVG templateId={step.template_id} size={140} />
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => setPaused((p) => !p)}
                data-testid="ceremony-pause"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline text-sm tab-link hover:bg-[hsl(var(--sand))]"
              >
                {paused ? <Play size={13} /> : <Pause size={13} />}
                {paused ? "Resume" : "Pause"}
              </button>
              <button
                onClick={skip}
                data-testid="ceremony-skip"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm tab-link text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
              >
                <SkipForward size={13} />
                Skip step
              </button>
            </div>

            <ul className="mt-8 flex items-center justify-center gap-2" aria-hidden>
              {steps.map((_, i) => (
                <li
                  key={i}
                  className={`w-6 h-1 rounded-full ${i < idx ? "bg-[hsl(var(--clay))]" : i === idx ? "bg-[hsl(var(--terracotta))]" : "bg-[hsl(var(--sand))]"}`}
                />
              ))}
            </ul>
          </>
        ) : (
          <div className="mt-6 text-center py-8" data-testid="ceremony-finished">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--moss))] text-[hsl(var(--paper))] mb-4">
              <Check size={26} strokeWidth={2} />
            </div>
            <h2 className="font-display text-3xl md:text-4xl italic leading-tight">You cultivated the face today.</h2>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              The four steps are marked complete. Return tomorrow — the ceremony waits.
            </p>
            <button
              onClick={onClose}
              data-testid="ceremony-done"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90 text-sm"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function CountdownRing({ seconds, pct }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = c - (c * pct) / 100;
  return (
    <div className="relative w-[110px] h-[110px]" data-testid="ceremony-ring">
      <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
        <circle cx="50" cy="50" r={r} stroke="hsl(var(--sand))" strokeWidth="6" fill="none" />
        <circle
          cx="50"
          cy="50"
          r={r}
          stroke="hsl(var(--terracotta))"
          strokeWidth="6"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center font-display text-3xl tabular-nums leading-none" data-testid="ceremony-seconds">
        {seconds}
      </div>
    </div>
  );
}
